/**
 * workspacePref — localStorage-backed persistence of the writer's
 * last-used workspace id. Spec 23 W1.
 *
 * Restored on shell mount; re-saved every successful workspace pick.
 * Stored id may be stale (workspace removed / re-org / different
 * account) — callers must validate against the live ``getWorkspaces``
 * list before using.
 *
 * SRP: pure storage primitive. No fetching, no UI. Tests can stub
 * the underlying ``Storage`` via a small interface.
 *
 * # DECISION: same persistence pattern as runResume (Spec 18 P1).
 * # WHY: localStorage is the durable, no-Office.js boundary that
 * #      every variant-c service uses for writer prefs. Matches the
 * #      established mental model — when the writer re-opens the
 * #      addin tomorrow, things are where they left them.
 * # DATE: 2026-04-22
 *
 * # DECISION: fail-soft on Storage absence (SSR / Office WebView
 * #   edge cases) — return null, do not throw.
 * # WHY: this is a writer-pref nicety, not a load-bearing primitive.
 * #      The addin must boot whether or not localStorage is present.
 * # DATE: 2026-04-22
 */

const STORAGE_KEY = "variantC.lastWorkspaceId";

/** Bounded surface so tests + callers can stub without touching the
 *  real Storage. Only the methods we use are part of the interface. */
export interface WorkspacePrefStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The default storage — guarded so SSR / no-localStorage boots
 *  return a no-op shim. */
function defaultStorage(): WorkspacePrefStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    // Some Office.js sandboxes throw on localStorage access.
    return null;
  }
}

/**
 * Read the persisted last-workspace id. Returns null when nothing is
 * stored, when storage is unavailable, or when the stored value is
 * an empty string. Callers must still verify the id appears in the
 * live ``getWorkspaces`` list.
 */
export function loadLastWorkspaceId(storage: WorkspacePrefStorage | null = defaultStorage()): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null || raw.length === 0) return null;
    return raw;
  } catch {
    return null;
  }
}

/**
 * Persist the writer's chosen workspace id. No-op on storage
 * absence or write failure — never throws. Empty / falsy ids are
 * coerced to a clear via ``removeItem``.
 */
export function saveLastWorkspaceId(id: string | null, storage: WorkspacePrefStorage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    if (!id) {
      storage.removeItem(STORAGE_KEY);
      return;
    }
    storage.setItem(STORAGE_KEY, id);
  } catch {
    // Best-effort — storage quota or sandbox restrictions.
  }
}

/** Drop the persisted value entirely. Used on explicit reset. */
export function clearLastWorkspaceId(storage: WorkspacePrefStorage | null = defaultStorage()): void {
  saveLastWorkspaceId(null, storage);
}

// ---------------------------------------------------------------------------
// Pure resolver — picks the best workspace given the live list + a
// possibly-stored preference. Pure so tests pin every branch.
// ---------------------------------------------------------------------------

export interface WorkspacePick {
  /** The id we chose. Null when ``workspaces`` is empty. */
  id: string | null;
  /** True when the persisted id was honoured. False when we fell
   *  back (no pref, pref absent from list, etc.) — useful for the
   *  shell to log a "preference dropped" warning. */
  matchedPreference: boolean;
}

export function pickInitialWorkspace(
  workspaces: ReadonlyArray<{ id: string; is_default?: boolean }>,
  preferredId: string | null,
): WorkspacePick {
  if (workspaces.length === 0) {
    return { id: null, matchedPreference: false };
  }
  if (preferredId) {
    const match = workspaces.find((w) => w.id === preferredId);
    if (match) return { id: match.id, matchedPreference: true };
  }
  // No preference: the Personal Workspace, where a self-serve user's own
  // sources live — not whichever workspace the server happened to list first.
  const personal = workspaces.find((w) => w.is_default === true);
  return { id: (personal ?? workspaces[0]).id, matchedPreference: false };
}
